import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle, XCircle, RotateCcw, Filter, ExternalLink, User, Calendar, Globe, Share, SlidersHorizontal } from "lucide-react";
import { useRequests } from "@/hooks/useRequests";
import { Request, RequestFilters } from "@/types/requests";
import { format } from "date-fns";

export const RequestsSection = () => {
  const [filters, setFilters] = useState<Partial<RequestFilters>>({
    status: [],
    requestType: [],
    searchTerm: ""
  });
  const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  const { requests, isLoading } = useRequests(filters);

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'pending':
        return 'secondary';
      case 'approved':
        return 'default';
      case 'rejected':
        return 'destructive';
      case 'recategorized':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  const getRequestTypeLabel = (type: string) => {
    switch (type) {
      case 'access':
        return 'Access Request';
      case 'category_change':
        return 'Category Change';
      case 'whitelist':
        return 'Whitelist Request';
      default:
        return type;
    }
  };

  const handleRequestAction = (request: Request, action: 'approve' | 'reject' | 'recategorize') => {
    console.log(`${action} request:`, request._id);
    // This would normally update the request via API
    setIsDetailOpen(false);
  };

  const handleViewDetails = (request: Request) => {
    setSelectedRequest(request);
    setIsDetailOpen(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
        <div className="h-64 bg-gray-200 rounded animate-pulse"></div>
      </div>
    );
  }

  return (
    <div className="px-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <h2 className="text-xl font-semibold text-gray-900">Requests</h2>
          <Badge variant="outline">{requests.length} requests</Badge>
        </div>
        <div className="flex items-center space-x-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            <SlidersHorizontal className="w-4 h-4 mr-2" />
            Filter
          </Button>
          <Button variant="outline" size="sm">
            <Share className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium">Search</label>
              <Input
                placeholder="Search by URL, user, or domain"
                value={filters.searchTerm || ""}
                onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Status</label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="recategorized">Recategorized</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Request Type</label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="access">Access Request</SelectItem>
                  <SelectItem value="category_change">Category Change</SelectItem>
                  <SelectItem value="whitelist">Whitelist Request</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button 
                variant="outline" 
                onClick={() => setFilters({ status: [], requestType: [], searchTerm: "" })}
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Requests Table */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Requests</CardTitle>
          <CardDescription>Click on a request to view details and take action</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table className="min-w-[700px]">
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>URL</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Time</TableHead>
                <TableHead className="w-20">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map((request) => (
                <TableRow key={request._id} className="cursor-pointer hover:bg-gray-50">
                  <TableCell>
                    <div>
                      <div className="font-medium">{request.userName}</div>
                      <div className="text-sm text-gray-500">{request.deviceName}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="max-w-xs">
                      <div className="font-mono text-sm truncate">{request.url}</div>
                      <div className="text-sm text-gray-500">{request.domain}</div>
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <Badge variant="outline">
                      {getRequestTypeLabel(request.requestType)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={getStatusVariant(request.status)}>
                      {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {format(new Date(request.timestamp), 'MMM d, HH:mm')}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewDetails(request)}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
             </TableBody>
           </Table>
           </div>
         </CardContent>
      </Card>

      {/* Request Detail Sheet */}
      <Sheet open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <SheetContent className="sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>Request Details</SheetTitle>
            <SheetDescription>
              Review and take action on this access request
            </SheetDescription>
          </SheetHeader>
          
          {selectedRequest && (
            <div className="space-y-6 mt-6">
              {/* Request Info */}
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <User className="w-4 h-4 text-gray-400" />
                  <div>
                    <div className="font-medium">{selectedRequest.userName}</div>
                    <div className="text-sm text-gray-500">{selectedRequest.deviceName}</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Globe className="w-4 h-4 text-gray-400" />
                  <div>
                    <div className="font-mono text-sm">{selectedRequest.url}</div>
                    <div className="text-sm text-gray-500">Domain: {selectedRequest.domain}</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <div className="text-sm">
                    {format(new Date(selectedRequest.timestamp), 'MMM d, yyyy HH:mm:ss')}
                  </div>
                </div>
              </div>

              {/* Request Details */}
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Request Type</label>
                  <p className="text-sm text-gray-600">{getRequestTypeLabel(selectedRequest.requestType)}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium">Category</label>
                  <p className="text-sm text-gray-600">{selectedRequest.category}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium">Reason</label>
                  <p className="text-sm text-gray-600">{selectedRequest.reason}</p>
                </div>

                <div>
                  <label className="text-sm font-medium">Current Status</label>
                  <div className="mt-1">
                    <Badge variant={getStatusVariant(selectedRequest.status)}>
                      {selectedRequest.status.charAt(0).toUpperCase() + selectedRequest.status.slice(1)}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Actions */}
              {selectedRequest.status === 'pending' && (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Admin Notes</label>
                    <Textarea placeholder="Add notes about your decision (optional)" />
                  </div>
                  
                  <div className="flex space-x-2">
                    <Button 
                      className="flex-1"
                      onClick={() => handleRequestAction(selectedRequest, 'approve')}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => handleRequestAction(selectedRequest, 'recategorize')}
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Recategorize
                    </Button>
                    <Button 
                      variant="destructive" 
                      className="flex-1"
                      onClick={() => handleRequestAction(selectedRequest, 'reject')}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                </div>
              )}

              {/* Resolution Info */}
              {selectedRequest.status !== 'pending' && selectedRequest.resolvedBy && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium mb-2">Resolution Details</h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div>Resolved by: {selectedRequest.resolvedBy}</div>
                    {selectedRequest.resolvedAt && (
                      <div>
                        Resolved at: {format(new Date(selectedRequest.resolvedAt), 'MMM d, yyyy HH:mm')}
                      </div>
                    )}
                    {selectedRequest.adminNotes && (
                      <div>Notes: {selectedRequest.adminNotes}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};